import { getApiBaseUrl } from '@/services/api/base-url';
import { getAnalyticsMetaProperties } from '@/services/analytics/meta-properties';

// Anonymous-visitor id. Must match Ember's key (`services/analytics.ts`
// `ANON_ID_KEY`) byte-for-byte: the authenticated Ember app reads the same
// localStorage entry to `alias(anon_id → userId)` after login, stitching a
// visitor's pre-auth events to their real identity. A different key (or a
// missing id) breaks that stitch.
const ANON_ID_KEY = 'cio:anonymous-id';

// Holds the id for this page load when localStorage can't. Without it, every
// call in the fallback path would mint a fresh id, so the several events in one
// login visit would each carry a different `anon_id` and stitching would break.
let volatileAnonId: string | undefined;

/**
 * Read (or lazily create) the anonymous visitor id, mirroring Ember's
 * `getOrSetAnonId`. Falls back to a per-page-load volatile id if localStorage is
 * unavailable (private mode / disabled) so the event still carries a *stable*
 * `anon_id` across the visit.
 */
function getOrSetAnonId(): string {
  try {
    const existing = window.localStorage.getItem(ANON_ID_KEY);
    if (existing != null && existing !== '') {
      return existing;
    }
    const id = window.crypto.randomUUID();
    window.localStorage.setItem(ANON_ID_KEY, id);
    return id;
  } catch {
    volatileAnonId ??= window.crypto.randomUUID();
    return volatileAnonId;
  }
}

/**
 * Fire a pre-auth login analytics event.
 *
 * The login page runs before any workspace exists, so it can't use Hydra's
 * workspace-scoped `useAnalytics` (which posts to the environment-scoped
 * tracking endpoint). Instead we mirror what the Ember login did: post directly
 * to the global endpoint with the same event names, the standard
 * `getAnalyticsMetaProperties()` block every Hydra/Ember event carries, the
 * per-event properties, and the `anon_id` (so anonymous sessions stitch to the
 * user on the next `alias`). Failures are swallowed — analytics is best-effort
 * and must never block or surface on the login form.
 *
 * Posts to `track_anon` with the `{ anon_id, event, properties }` body Ember's
 * anonymous tracking used. `getApiBaseUrl()` resolves the same
 * `{us,eu}.fly.customer.io` host Ember's global-scope adapter hit, so this is
 * the identical endpoint the Ember login posted to.
 */

/**
 * The login URL can carry a 2FA email-link `token` and `email` in its query
 * string, and `onNavigateStep` preserves those across step changes — so the raw
 * `url`/`search` meta properties would leak a still-valid login credential into
 * analytics. Strip them from both before reporting. (Ember sent the full href;
 * this closes that off.)
 */
function sanitizedLocation(): { url: string; search: string } {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  url.searchParams.delete('email');
  return { url: url.toString(), search: url.search };
}

export function trackLoginEvent(event: string, properties: Record<string, unknown> = {}): void {
  void fetch(`${getApiBaseUrl()}/v1/track_anon`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      anon_id: getOrSetAnonId(),
      event,
      properties: { ...getAnalyticsMetaProperties(), ...sanitizedLocation(), ...properties },
    }),
  }).catch(() => {
    // Analytics is best-effort; never block or surface login on a tracking failure.
  });
}

export const LoginAnalyticsEvent = {
  SignInWithDifferentEmailClicked: 'User: Sign in With Different Email Clicked',
  SignInWithGoogleClicked: 'User: Sign in With Google Clicked',
  ChooseUserListViewed: 'User: Choose User List Viewed',
  LoggedIntoNewAccount: 'Multi-Account: User Logged Into New Account',
} as const;
